/**
 * NOS-51: Hooks for blog posts (list, single, create, update, delete).
 * Requires can_manage_content permission (enforced by RLS and UI).
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database, Json } from "@/types/database"
import { generateSlug } from "@/lib/slugUtils"
import { queuePublicCachePurge } from "@/lib/publicCache"

type BlogPost = Database["public"]["Tables"]["blog_posts"]["Row"]
type BlogPostInsert = Database["public"]["Tables"]["blog_posts"]["Insert"]
type BlogPostUpdate = Database["public"]["Tables"]["blog_posts"]["Update"]
type BlogCategory = Database["public"]["Tables"]["blog_categories"]["Row"]

export interface BlogPostWithCategory extends BlogPost {
  blog_categories: Pick<BlogCategory, "id" | "name" | "slug" | "color"> | null
}

export type BlogPostStatus = "all" | "draft" | "published" | "scheduled"

export interface UseBlogPostsFilters {
  categoryId?: string | null
  status?: BlogPostStatus
}

const PAGE_SIZE = 50

function getStatus(
  isPublished: boolean,
  publishedAt: string | null
): "draft" | "published" | "scheduled" {
  if (!isPublished) return "draft"
  if (!publishedAt) return "draft"
  const pub = new Date(publishedAt)
  return pub > new Date() ? "scheduled" : "published"
}

export function useBlogPosts(orgId: string | null, filters?: UseBlogPostsFilters) {
  const [posts, setPosts] = useState<BlogPostWithCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setPosts([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from("blog_posts")
        .select("*")
        .eq("org_id", orgId)
        .order("updated_at", { ascending: false })
        .limit(PAGE_SIZE)

      const statusFilter = filters?.status
      if (statusFilter && statusFilter !== "all") {
        if (statusFilter === "draft") {
          query = query.eq("is_published", false)
        } else if (statusFilter === "published") {
          query = query
            .eq("is_published", true)
            .not("published_at", "is", null)
            .lte("published_at", new Date().toISOString())
        } else if (statusFilter === "scheduled") {
          query = query
            .eq("is_published", true)
            .gt("published_at", new Date().toISOString())
        }
      }

      const categoryId = filters?.categoryId
      if (categoryId) {
        query = query.eq("category_id", categoryId)
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        setError(fetchError.message)
        setPosts([])
        return
      }

      const rows = (data ?? []) as BlogPost[]
      setPosts(
        rows.map((p) => ({
          ...p,
          blog_categories: null as BlogPostWithCategory["blog_categories"],
        }))
      )
    } finally {
      setLoading(false)
    }
  }, [orgId, filters?.categoryId, filters?.status])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { posts, loading, error, refresh }
}

export function useBlogPost(orgId: string | null, postId: string | null) {
  const [post, setPost] = useState<BlogPostWithCategory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !postId) {
      setPost(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("blog_posts")
      .select("*")
      .eq("id", postId)
      .eq("org_id", orgId)
      .maybeSingle()

    if (fetchError) {
      setError(fetchError.message)
      setPost(null)
    } else {
      const row = data as BlogPost | null
      setPost(
        row
          ? { ...row, blog_categories: null as BlogPostWithCategory["blog_categories"] }
          : null
      )
    }
    setLoading(false)
  }, [orgId, postId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const status = post
    ? getStatus(post.is_published, post.published_at)
    : null

  return { post, loading, error, refresh, status }
}

export function useCreateBlogPost(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createBlogPost = useCallback(
    async (input: {
      title: string
      slug?: string
      excerpt?: string | null
      cover_image?: string | null
      category_id?: string | null
      author_id?: string | null
      is_published?: boolean
      published_at?: string | null
      content_sections?: Json
      seo_title?: string | null
      seo_description?: string | null
      og_image?: string | null
    }): Promise<BlogPost | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)

      const slug =
        input.slug?.trim() || generateSlug(input.title) || "untitled-post"

      const insert: BlogPostInsert = {
        org_id: orgId,
        title: input.title.trim(),
        slug,
        excerpt: input.excerpt?.trim() || null,
        cover_image: input.cover_image || null,
        content_sections: (input.content_sections ?? []) as Json,
        category_id: input.category_id || null,
        author_id: input.author_id || null,
        is_published: input.is_published ?? false,
        published_at: input.published_at ?? null,
        seo_title: input.seo_title?.trim() || null,
        seo_description: input.seo_description?.trim() || null,
        og_image: input.og_image?.trim() || null,
      }

      const { data, error: insertError } = await supabase
        .from("blog_posts")
        .insert(insert)
        .select()
        .single()

      setLoading(false)
      if (insertError) {
        setError(
          insertError.code === "23505"
            ? "A post with this URL already exists. Use a different slug."
            : insertError.message
        )
        return null
      }
      const post = data as BlogPost
      if (post.is_published) {
        queuePublicCachePurge(orgId, { type: "blog-post-publish", postSlug: post.slug })
      }
      return post
    },
    [orgId]
  )

  return { createBlogPost, loading, error }
}

export function useUpdateBlogPost(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateBlogPost = useCallback(
    async (id: string, updates: BlogPostUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: updateError } = await supabase
        .from("blog_posts")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (updateError) {
        setError(
          updateError.code === "23505"
            ? "A post with this URL already exists. Use a different slug."
            : updateError.message
        )
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        // Publish-state flips also refresh the sitemap; plain content saves only the pages.
        const trigger =
          updates.is_published === true
            ? ("blog-post-publish" as const)
            : updates.is_published === false
              ? ("blog-post-unpublish" as const)
              : ("blog-post-save" as const)
        queuePublicCachePurge(orgId, { type: trigger, postSlug: slug })
      }
      return true
    },
    [orgId]
  )

  return { updateBlogPost, loading, error }
}

export function useDeleteBlogPost(orgId: string | null) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deleteBlogPost = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: deleteError } = await supabase
        .from("blog_posts")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (deleteError) {
        setError(deleteError.message)
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, { type: "blog-post-delete", postSlug: slug })
      }
      return true
    },
    [orgId]
  )

  return { deleteBlogPost, loading, error }
}

export { getStatus }
